import { Download, Loader2, RotateCw } from "lucide-react";
import React, { useEffect, useState } from "react";
import agreementsService, { type Agreement } from "../../services/agreementsService";
import blobService from "../../services/blobService";

export const CUSTOMER_AGREEMENT_FOLDER = "Customer Agreement";

const STANDARD_AGREEMENT = {
  filename: "Namsoft Advisors Investment Advisory Agreement_latest.pdf",
  name: "investment-advisory-agreement",
} as const;

const RETIREMENT_AGREEMENT = {
  filename: "Namsoft Advisors Investment Advisory Agreement_Retirement Accounts_latest.pdf",
  name: "investment-advisory-agreement-retirement",
} as const;

export interface InvestmentAdvisoryAgreementConfig {
  filename: string;
  name: string;
}

export const getInvestmentAdvisoryAgreementConfig = (
  isRetirementAccount: boolean
): InvestmentAdvisoryAgreementConfig => (
  isRetirementAccount ? RETIREMENT_AGREEMENT : STANDARD_AGREEMENT
);

const normalizeAgreementSignerName = (value: string): string => (
  value.trim().toLowerCase()
);

export const agreementSignerNamesMatch = (
  enteredName: string,
  expectedName: string
): boolean => {
  const normalizedExpectedName = normalizeAgreementSignerName(expectedName);
  return Boolean(
    normalizedExpectedName
    && normalizeAgreementSignerName(enteredName) === normalizedExpectedName
  );
};

export const hasMatchingInvestmentAdvisoryAgreement = (
  agreements: Agreement[],
  agreement: InvestmentAdvisoryAgreementConfig,
  signerName: string
): boolean => {
  const expectedDocument = `${CUSTOMER_AGREEMENT_FOLDER}/${agreement.filename}`;
  return agreements.some((savedAgreement) => (
    savedAgreement.agreementName === agreement.name
    && savedAgreement.agreementDocument === expectedDocument
    && typeof savedAgreement.data?.signedName === "string"
    && agreementSignerNamesMatch(savedAgreement.data.signedName, signerName)
  ));
};

interface InvestmentAdvisoryAgreementProps {
  agreement: InvestmentAdvisoryAgreementConfig;
  canSign: boolean;
  onSignComplete: () => void;
  signerName: string;
  previewOnly?: boolean;
}

const InvestmentAdvisoryAgreement: React.FC<
  InvestmentAdvisoryAgreementProps
> = ({
  agreement,
  canSign,
  onSignComplete,
  signerName,
  previewOnly = false,
}) => {
  const [pdfBlobUrl, setPdfBlobUrl] = useState<string | null>(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [pdfReloadKey, setPdfReloadKey] = useState(0);
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerLoadTimedOut, setViewerLoadTimedOut] = useState(false);
  const [viewerKey, setViewerKey] = useState(0);
  const [hasConfirmedReading, setHasConfirmedReading] = useState(false);
  const [signatureName, setSignatureName] = useState("");
  const [signing, setSigning] = useState(false);
  const [signError, setSignError] = useState<string | null>(null);
  const [showNameMismatch, setShowNameMismatch] = useState(false);

  useEffect(() => {
    if (previewOnly) return undefined;

    let cancelled = false;
    let objectUrl: string | null = null;
    setPdfLoading(true);
    setPdfError(null);
    setPdfBlobUrl(null);
    setViewerLoading(false);
    setViewerLoadTimedOut(false);

    void blobService.downloadRunPlutusFile(
      CUSTOMER_AGREEMENT_FOLDER,
      agreement.filename
    ).then((blob) => {
      if (cancelled) return;
      objectUrl = URL.createObjectURL(blob);
      setViewerLoading(true);
      setPdfBlobUrl(objectUrl);
    }).catch((error: unknown) => {
      if (!cancelled) {
        setPdfError(error instanceof Error ? error.message : "Failed to load document");
      }
    }).finally(() => {
      if (!cancelled) setPdfLoading(false);
    });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [agreement.filename, pdfReloadKey, previewOnly]);

  useEffect(() => {
    if (!pdfBlobUrl || !viewerLoading) return undefined;

    const timer = window.setTimeout(() => setViewerLoadTimedOut(true), 10_000);
    return () => window.clearTimeout(timer);
  }, [pdfBlobUrl, viewerKey, viewerLoading]);

  useEffect(() => {
    if (!signatureName.trim() || agreementSignerNamesMatch(signatureName, signerName)) {
      setShowNameMismatch(false);
      return undefined;
    }

    const timer = window.setTimeout(() => setShowNameMismatch(true), 2000);
    return () => window.clearTimeout(timer);
  }, [signatureName, signerName]);

  const isSignatureValid = agreementSignerNamesMatch(signatureName, signerName);

  const handleSign = async () => {
    if (!isSignatureValid || signing) return;

    setSigning(true);
    setSignError(null);

    try {
      if (!previewOnly) {
        await agreementsService.signAgreement({
          agreementName: agreement.name,
          agreementDocument: `${CUSTOMER_AGREEMENT_FOLDER}/${agreement.filename}`,
          data: {
            signedAt: new Date().toISOString(),
            signedName: signatureName.trim(),
            userAgent: navigator.userAgent,
          },
        });
      }
      onSignComplete();
    } catch (error) {
      setSignError(error instanceof Error ? error.message : "Failed to sign agreement");
    } finally {
      setSigning(false);
    }
  };

  const reloadViewer = () => {
    setViewerLoading(true);
    setViewerLoadTimedOut(false);
    setViewerKey((current) => current + 1);
  };

  const reloadDocument = () => {
    setPdfReloadKey((current) => current + 1);
  };

  const documentReady = previewOnly || Boolean(pdfBlobUrl);

  return (
    <div className="border-t border-gray-200 dark:border-rp-separator">
          {pdfLoading && (
            <div className="flex h-32 items-center justify-center" role="status">
              <Loader2 className="h-8 w-8 animate-spin text-rp-amber" aria-hidden="true" />
              <span className="sr-only">Loading agreement</span>
            </div>
          )}

          {pdfError && (
            <div className="p-6">
              <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400">
                <p>Failed to load document: {pdfError}</p>
                <button
                  type="button"
                  onClick={reloadDocument}
                  className="mt-3 inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-red-300 bg-white px-4 font-medium text-red-700 transition-colors hover:border-red-400 dark:border-red-800 dark:bg-rp-surface dark:text-red-400"
                >
                  <RotateCw className="h-4 w-4" aria-hidden="true" />
                  Retry document load
                </button>
              </div>
            </div>
          )}

          {previewOnly && (
            <div className="border-b border-gray-200 bg-gray-50 p-6 text-sm text-gray-600 dark:border-rp-separator dark:bg-rp-surface-raised dark:text-gray-300">
              Agreement document preview is disabled in local preview.
            </div>
          )}

          {!previewOnly && !pdfLoading && !pdfError && pdfBlobUrl && (
            <>
              <div className="flex flex-wrap items-center justify-end gap-2 border-b border-gray-200 bg-gray-50 p-3 dark:border-rp-separator dark:bg-rp-surface-raised">
                <button
                  type="button"
                  onClick={reloadViewer}
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 text-sm font-medium text-gray-700 transition-colors hover:border-gray-300 dark:border-rp-separator dark:bg-rp-surface dark:text-gray-300 dark:hover:border-gray-600"
                >
                  <RotateCw className="h-4 w-4" aria-hidden="true" />
                  Reload viewer
                </button>
                <a
                  href={pdfBlobUrl}
                  download={agreement.filename}
                  className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-rp-amber px-4 text-sm font-medium text-white transition-colors hover:bg-yellow-600"
                >
                  <Download className="h-4 w-4" aria-hidden="true" />
                  Download PDF
                </a>
              </div>

              <div className="relative h-[52vh] min-h-[360px] bg-white md:h-[500px] dark:bg-rp-surface">
                {viewerLoading && (
                  <div
                    role="status"
                    className="absolute inset-0 z-10 flex flex-col items-center justify-center gap-3 bg-white text-sm text-gray-600 dark:bg-rp-surface dark:text-gray-300"
                  >
                    <Loader2 className="h-8 w-8 animate-spin text-rp-amber" aria-hidden="true" />
                    <span>
                      {viewerLoadTimedOut
                        ? "The viewer is taking longer than expected."
                        : "Loading agreement viewer"}
                    </span>
                    {viewerLoadTimedOut && (
                      <button
                        type="button"
                        onClick={reloadViewer}
                        className="inline-flex min-h-10 items-center justify-center gap-2 rounded-lg bg-rp-amber px-4 text-sm font-medium text-white transition-colors hover:bg-yellow-600"
                      >
                        <RotateCw className="h-4 w-4" aria-hidden="true" />
                        Try again
                      </button>
                    )}
                  </div>
                )}
                <iframe
                  key={viewerKey}
                  src={`${pdfBlobUrl}#navpanes=0&toolbar=1&view=FitH`}
                  title="Investment Advisory Agreement document"
                  onLoad={() => {
                    setViewerLoading(false);
                    setViewerLoadTimedOut(false);
                  }}
                  className="h-full w-full border-0"
                />
              </div>
            </>
          )}

          {documentReady && canSign && (
            <div className="border-t border-v7-ink/15 bg-v7-white p-4 md:p-6">
              <div className="mx-auto max-w-xl space-y-4">
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={hasConfirmedReading}
                    onChange={(event) => setHasConfirmedReading(event.target.checked)}
                    className="mt-1 h-5 w-5 rounded border-2 border-v7-ink/20 bg-v7-white text-v7-bronze-dark focus:ring-v7-bronze-dark"
                  />
                  <span className="text-sm text-v7-ink-soft">
                    I confirm that I have read the entire Investment Advisory Agreement
                  </span>
                </label>

                {hasConfirmedReading && (
                  <>
                    <div>
                      <label
                        htmlFor="investment-advisory-agreement-signature"
                        className="mb-2 block text-sm font-medium text-v7-ink-soft"
                      >
                        Type your name exactly as{" "}
                        <span className="font-semibold text-v7-bronze-dark">{signerName}</span>
                      </label>
                      <input
                        id="investment-advisory-agreement-signature"
                        type="text"
                        value={signatureName}
                        onChange={(event) => {
                          setSignatureName(event.target.value);
                          setShowNameMismatch(false);
                        }}
                        placeholder="Enter your full name"
                        autoComplete="off"
                        className={`w-full rounded-lg border bg-v7-white px-4 py-3 text-base text-v7-ink placeholder:text-v7-ink/40 focus:outline-none focus:ring-2 focus:ring-v7-bronze-dark/20 ${
                          showNameMismatch
                            ? "border-red-500"
                            : "border-v7-ink/20 focus:border-v7-bronze-dark"
                        }`}
                      />
                      {showNameMismatch && (
                        <p className="mt-1 text-sm text-red-500">
                          Name does not match. Please type exactly as shown.
                        </p>
                      )}
                    </div>

                    <p className="text-xs leading-relaxed text-v7-ink/60">
                      By typing your name and clicking "Agree & Continue", you acknowledge this
                      is a binding legal contract with Namsoft Advisors.
                    </p>

                    {signError && (
                      <div
                        role="alert"
                        className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-400"
                      >
                        {signError}
                      </div>
                    )}

                    <button
                      type="button"
                      onClick={handleSign}
                      disabled={!isSignatureValid || signing}
                      className={`flex w-full items-center justify-center gap-2 rounded-lg px-4 py-3 font-medium ${
                        isSignatureValid && !signing
                          ? "bg-v7-bronze-dark text-v7-white hover:bg-rp-amber"
                          : "cursor-not-allowed border border-v7-ink/15 bg-v7-mist text-v7-ink/40"
                      }`}
                    >
                      {signing ? (
                        <>
                          <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
                          Processing...
                        </>
                      ) : (
                        "Agree & Continue"
                      )}
                    </button>
                  </>
                )}
              </div>
            </div>
          )}

    </div>
  );
};

export default InvestmentAdvisoryAgreement;
